/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import {getPathWithLocale} from '../../utils/url'
import Link from '../link'

import {
    Box,
    Divider,
    Flex,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useLocation} from 'react-router-dom'
import {languageAliases, styleConfigChakraKeys} from '../../constants'
import FooterAccordion from './footer-accordion'
import Subscribe from './subscribe'
import {useBooxi} from '../../hooks/use-booxi'

const NewsletterBar = ({
    newsLetterEmail,
    languageSwitcher,
    cta,
    listCollection
}) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.footer)
    const {buildUrl} = useMultiSite()
    const {pathname} = useLocation()

    const [selectedLocale, setSelectedLocale] = useState(
        languageAliases.en.localeName
    )

    const {instantiateBooxi} = useBooxi()

    const switchLocale = (locale) => {
        localStorage?.setItem('locale', languageAliases[locale].locale)
        const newUrl = getPathWithLocale(
            languageAliases[locale.toLowerCase()].locale,
            buildUrl,
            {
                disallowParams: ['refine']
            }
        )
        window.location = newUrl
        setSelectedLocale(locale)
    }

    useEffect(() => {
        if (pathname.match(languageAliases.fr.path)) {
            setSelectedLocale(languageAliases.fr.localeName)
        }
    }, [pathname])

    return (
        <Box {...styles.footerSectionContainer}>
            <Box display={{base: 'none', lg: 'flex'}}>
                {newsLetterEmail && (
                    <Subscribe
                        title={newsLetterEmail.heading}
                        inputPlaceholder={
                            newsLetterEmail.fieldsCollection.items[0]
                                .placeholder
                        }
                    />
                )}
            </Box>

            <Box display={{base: 'block', lg: 'none'}}>
                {newsLetterEmail && (
                    <Subscribe
                        title={newsLetterEmail.heading}
                        inputPlaceholder={
                            newsLetterEmail.fieldsCollection.items[0]
                                .placeholder
                        }
                    />
                )}
                <Box width="100%">
                    <Divider {...styles.divider} />
                    <FooterAccordion lists={listCollection} />
                    <Divider {...styles.divider} />
                </Box>
            </Box>

            {(languageSwitcher || cta) && (
                <Flex
                    gap={{md: '30px'}}
                    alignItems="center"
                    justifyContent={{base: 'space-between', lg: 'center'}}
                    width={{base: '100%', lg: 'auto'}}
                >
                    {cta && (
                        <Link
                            {...cta}
                            buttonStyles={{fontSize: '16px', height: '44px'}}
                            onLinkClick={instantiateBooxi}
                        />
                    )}
                    {languageSwitcher && (
                        <Flex gap="8px" alignItems="center">
                            <Text fontSize="16px" color="black">
                                {languageSwitcher.label}
                            </Text>
                            {languageSwitcher.options.map((locale) => (
                                <Link
                                    key={locale}
                                    type="primary"
                                    onLinkClick={() => switchLocale(locale)}
                                    buttonStyles={{
                                        padding: '10px',
                                        width: '38px',
                                        height: '38px',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        textTransform: 'uppercase',
                                        fontSize: '16px'
                                    }}
                                    inactive={
                                        locale.toLowerCase() !==
                                        selectedLocale.toLowerCase()
                                    }
                                    text={locale}
                                />
                            ))}
                        </Flex>
                    )}
                </Flex>
            )}
        </Box>
    )
}

NewsletterBar.propTypes = {
    newsLetterEmail: PropTypes.shape({
        heading: PropTypes.string,
        submit: PropTypes.shape({
            text: PropTypes.string,
            url: PropTypes.string,
            openLinkInNewTab: PropTypes.bool
        }),
        fieldsCollection: PropTypes.shape({
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    type: PropTypes.string,
                    label: PropTypes.string,
                    required: PropTypes.bool,
                    placeholder: PropTypes.string
                })
            )
        })
    }),
    languageSwitcher: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
        name: PropTypes.string,
        errorMessage: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string)
    }),
    cta: PropTypes.shape({
        text: PropTypes.string,
        url: PropTypes.string,
        openLinkInNewTab: PropTypes.bool,
        isExternal: PropTypes.bool,
        type: PropTypes.string
    }),
    listCollection: PropTypes.arrayOf(
        PropTypes.shape({
            heading: PropTypes.string,
            headingType: PropTypes.string,
            itemsCollection: PropTypes.shape({
                items: PropTypes.arrayOf(
                    PropTypes.shape({
                        items: PropTypes.arrayOf(
                            PropTypes.shape({
                                ...Link.propTypes
                            })
                        )
                    })
                )
            })
        })
    )
}

export default NewsletterBar
